import React from 'react';
import { Alert, Col, Row, Spin, Statistic, Table, Tag, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useCurrency } from '@/contexts/CurrencyContext';
import {
  filtersQueryKey,
  getSalesAnalyticsPresales,
  ownerIntelligencePresalesUrl,
  type SalesAnalyticsFilterParams,
} from '@/services/salesAnalytics.service';
import { OiHorizontalBarChart } from '@/components/owner-intelligence/charts/OiCharts';
import { SalesAnalyticsToolbar } from './SalesAnalyticsToolbar';

const { Title } = Typography;

export const SalesAnalyticsPresalesTab: React.FC<{
  filters: SalesAnalyticsFilterParams;
  filterSummary: string;
}> = ({ filters, filterSummary }) => {
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ['sales-analytics', 'presales', filtersQueryKey(filters)],
    queryFn: () => getSalesAnalyticsPresales(filters),
  });

  if (query.isLoading) return <Spin />;
  if (query.isError) return <Alert type="error" message={(query.error as Error).message} />;
  const d = query.data!;

  const awaitingOrder = d.unconverted_quotations.filter(
    (r) => r.commercial_stage !== 'won_pr_awaiting_so'
  );
  const prAwaitingSo = d.unconverted_quotations.filter(
    (r) => r.commercial_stage === 'won_pr_awaiting_so'
  );

  const printRows = d.unconverted_quotations.map((r) => ({
    quotation_number: r.quotation_number,
    commercial_stage: r.commercial_stage_label ?? r.status,
    customer: r.customer_name ?? '',
    grand_total: formatCurrency(r.grand_total),
    days_open: r.days_since_accepted ?? '',
    owner: r.owner_name ?? '',
  }));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <Title level={5} style={{ margin: 0 }}>
          Presales
        </Title>
        <SalesAnalyticsToolbar
          tab="presales"
          tabTitle="Presales"
          filters={filters}
          filterSummary={filterSummary}
          printColumns={[
            { key: 'quotation_number', label: 'Quotation' },
            { key: 'commercial_stage', label: 'Commercial outcome' },
            { key: 'customer', label: 'Customer' },
            { key: 'grand_total', label: 'Value', align: 'right' },
            { key: 'days_open', label: 'Days open' },
            { key: 'owner', label: 'Owner' },
          ]}
          printRows={printRows}
        />
      </div>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={
          <a onClick={() => navigate(ownerIntelligencePresalesUrl(filters))}>
            Open Owner Intelligence sales pipeline with same filters →
          </a>
        }
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Statistic title="Leads (period)" value={d.lead_stats.total} />
          <Statistic title="Qualified" value={d.lead_stats.qualified} suffix={`(${d.lead_stats.qualification_ratio_pct}%)`} />
          <Statistic title="Converted" value={d.lead_stats.converted} />
        </Col>
        <Col xs={24} md={8}>
          <Statistic title="Open opportunities" value={d.opportunity_pipeline.open_count} />
          <Statistic title="Pipeline value" value={formatCurrency(d.opportunity_pipeline.pipeline_value)} />
          <Statistic title="Weighted value" value={formatCurrency(d.opportunity_pipeline.weighted_value)} />
        </Col>
        <Col xs={24} md={8}>
          <Statistic title="Quote win rate" value={d.quotation_summary.win_rate_pct ?? '—'} suffix={d.quotation_summary.win_rate_pct != null ? '%' : undefined} />
          <Statistic title="Won / Lost / Open" value={`${d.quotation_summary.won} / ${d.quotation_summary.lost} / ${d.quotation_summary.open}`} />
          <Statistic title="Open quote pipeline" value={formatCurrency(d.quotation_summary.pipeline_value)} />
        </Col>
      </Row>

      <Title level={5} style={{ marginTop: 24 }}>
        Pipeline by industry
      </Title>
      <OiHorizontalBarChart
        data={d.pipeline_by_industry.map((r) => ({ name: r.industry, value: r.value }))}
        dataKey="value"
        nameKey="name"
        height={200}
      />

      <Title level={5} style={{ marginTop: 24 }}>
        Stale opportunities (30+ days without update)
      </Title>
      <Table
        size="small"
        rowKey="id"
        dataSource={d.stale_opportunities}
        pagination={{ pageSize: 10 }}
        locale={{ emptyText: 'No stale opportunities in filtered scope' }}
        columns={[
          { title: 'Opportunity', dataIndex: 'title' },
          { title: 'Customer', dataIndex: 'customer_name' },
          { title: 'Est. value', dataIndex: 'estimated_value', align: 'right', render: (v: number) => formatCurrency(v) },
          { title: '', key: 'link', width: 80, render: (_: unknown, r) => <a onClick={() => navigate(r.link_path)}>View</a> },
        ]}
      />

      <Title level={5} style={{ marginTop: 24 }}>
        Expiring quotations (next 14 days)
      </Title>
      <Table
        size="small"
        rowKey="id"
        dataSource={d.expiring_quotations}
        pagination={{ pageSize: 10 }}
        locale={{ emptyText: 'No expiring quotations in filtered scope' }}
        columns={[
          { title: 'Quotation', dataIndex: 'quotation_number' },
          { title: 'Customer', dataIndex: 'customer_name' },
          { title: 'Valid until', dataIndex: 'valid_until', width: 110 },
          { title: 'Value', dataIndex: 'grand_total', align: 'right', render: (v: number) => formatCurrency(v) },
          { title: '', key: 'link', width: 80, render: (_: unknown, r) => <a onClick={() => navigate(r.link_path)}>View</a> },
        ]}
      />

      <Title level={5} style={{ marginTop: 24 }}>
        Accepted — awaiting customer order ({awaitingOrder.length})
      </Title>
      <Table
        size="small"
        rowKey="id"
        dataSource={awaitingOrder}
        pagination={{ pageSize: 10 }}
        locale={{ emptyText: 'No accepted quotations awaiting a customer order' }}
        columns={[
          { title: 'Quotation', dataIndex: 'quotation_number' },
          { title: 'Customer', dataIndex: 'customer_name' },
          { title: 'Owner', dataIndex: 'owner_name', render: (v: string | null | undefined) => v || '—' },
          {
            title: 'Days open',
            dataIndex: 'days_since_accepted',
            width: 90,
            render: (d: number | undefined) => (d != null ? <Tag color={d > 30 ? 'red' : d > 14 ? 'orange' : 'green'}>{d}d</Tag> : '—'),
          },
          { title: 'Value', dataIndex: 'grand_total', align: 'right', render: (v: number) => formatCurrency(v) },
          { title: '', key: 'link', width: 80, render: (_: unknown, r) => <a onClick={() => navigate(r.link_path)}>View</a> },
        ]}
      />

      <Title level={5} style={{ marginTop: 24 }}>
        Won — PR booked, awaiting SO ({prAwaitingSo.length})
      </Title>
      <Table
        size="small"
        rowKey="id"
        dataSource={prAwaitingSo}
        pagination={{ pageSize: 10 }}
        locale={{ emptyText: 'No PR-booked quotations awaiting a sales order' }}
        columns={[
          { title: 'Quotation', dataIndex: 'quotation_number' },
          { title: 'Customer', dataIndex: 'customer_name' },
          {
            title: 'Outcome',
            dataIndex: 'commercial_stage_label',
            render: (v: string | undefined) => <Tag color="success">{v || 'Won — PR booked'}</Tag>,
          },
          {
            title: 'Days open',
            dataIndex: 'days_since_accepted',
            width: 90,
            render: (d: number | undefined) => (d != null ? <Tag color={d > 30 ? 'red' : 'orange'}>{d}d</Tag> : '—'),
          },
          { title: 'Value', dataIndex: 'grand_total', align: 'right', render: (v: number) => formatCurrency(v) },
          { title: '', key: 'link', width: 80, render: (_: unknown, r) => <a onClick={() => navigate(r.link_path)}>View</a> },
        ]}
      />
    </div>
  );
};
